import type {Image} from '@/api/bffSupport';
import type {AsyncStatus} from '@react-hookz/web';

import {useQuery} from '@tanstack/react-query';
import {useMemo} from 'react';

import {type HotelDetails, type ImageCategoryCode} from '@/api/hoteldetails';
import {getHotelDetailsFetchMethod} from '@/modules/common/hooks/useHotelDetails/utils';
import {useLocalSplit} from '@/modules/common/hooks/useLocalSplit';
import * as bffSupportQueries from '@/modules/common/queries/bffSupport';

// @FIXME try to get rid of this mapping eventually
const mapHotelImageToHotelDetailsImage = (
  hotelImage: Image
): HotelDetails['images'][0] => ({
  ...hotelImage,
  type: '',
  categoryCodes: (hotelImage.categoryCodes as Array<ImageCategoryCode>) ?? [],
  tourOperatorAndSeasons: hotelImage.tourOperatorAndSeasons ?? [],
});

const mapQueryStatus = (query: {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}): AsyncStatus => {
  if (query.isLoading) {
    return 'loading';
  } else if (query.isError) {
    return 'error';
  } else if (query.isSuccess) {
    return 'success';
  }
  return 'not-executed';
};

export const useHotelDetails = (
  hotelId: string
): [
  HotelDetails | undefined,
  {hotelDetails: AsyncStatus; hotelImages: AsyncStatus},
] => {
  const hotelContentApiFeatureTreatment = useLocalSplit<
    'supplierAttributes' | 'off' | 'on'
  >('FFP_5733_hotel_content_api').treatment;
  const isHotelContentApiFeatureOn =
    hotelContentApiFeatureTreatment === 'on' ||
    hotelContentApiFeatureTreatment === 'supplierAttributes';

  const hotelDetailsQueryOptionsMethod = getHotelDetailsFetchMethod({
    isHotelContentApiFeatureOn,
  });

  const hotelImagesQuery = useQuery(bffSupportQueries.hotelImages(hotelId));
  const hotelImagesQueryStatus = mapQueryStatus(hotelImagesQuery);
  // @ts-expect-error TS2769
  const hotelDetailsQuery = useQuery(hotelDetailsQueryOptionsMethod(hotelId));
  const hotelDetailsQueryStatus = mapQueryStatus(hotelDetailsQuery);

  const hotelDetails = useMemo<HotelDetails | undefined>(() => {
    const data = hotelDetailsQuery.data;

    if (!data) {
      return undefined;
    }

    return {
      ...data,
      images: (hotelImagesQuery.data ?? []).map((image) =>
        mapHotelImageToHotelDetailsImage(image)
      ),
    };
  }, [hotelDetailsQuery.data, hotelImagesQuery.data]);

  return [
    hotelDetails,
    {
      hotelDetails: hotelDetailsQueryStatus,
      hotelImages: hotelImagesQueryStatus,
    },
  ];
};
